/**
 * 日付フォーマットに関する共通ユーティリティ
 *
 * 現時点では日本（ja-JP）とアメリカ（en-US）のロケールに対応し、
 * その他の言語はアメリカ英語をデフォルトとして扱います。
 */

export type DateFormatPreset = 'dateMedium' | 'numericMonth' | 'numericDay';

const DEFAULT_LOCALE = 'en-US';
const JAPAN_LOCALE = 'ja-JP';

const DATE_FORMAT_OPTIONS: Record<DateFormatPreset, Intl.DateTimeFormatOptions> = {
    dateMedium: { dateStyle: 'medium' },
    numericMonth: { month: 'numeric' },
    numericDay: { day: 'numeric' },
};

// 外部（UserContextなど）から優先タイムゾーンを提供するためのプロバイダ
let timeZoneProvider: (() => string | undefined) | null = null;
export const setTimeZoneProvider = (provider: () => string | undefined): void => {
    timeZoneProvider = provider;
};

const getBrowserTimeZone = (): string | undefined => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return undefined;
    }
};

/**
 * 言語コードを対応するロケールに変換する
 * @param language i18nextで利用される言語コード
 * @returns Intl.DateTimeFormat向けのロケール
 */
export const resolveDateLocale = (language?: string): string => {
    if (!language) {
        return DEFAULT_LOCALE;
    }

    const normalized = language.toLowerCase();

    if (normalized.startsWith('ja')) {
        return JAPAN_LOCALE;
    }

    // 英語以外も現状はen-USを既定値として扱う
    return DEFAULT_LOCALE;
};

/**
 * DateTimeFormatをプリセットから生成する
 * @param language 言語コード
 * @param preset 使用するフォーマットプリセット
 * @param overrideOptions プリセットに追加で適用するオプション
 * @returns Intl.DateTimeFormat インスタンス
 */
export const createDateFormatter = (
    language: string | undefined,
    preset: DateFormatPreset,
    overrideOptions?: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat => {
    const locale = resolveDateLocale(language);
    const baseOptions = DATE_FORMAT_OPTIONS[preset];
    const preferredTimeZone = timeZoneProvider ? timeZoneProvider() : undefined;
    const browserTimeZone = preferredTimeZone ?? getBrowserTimeZone();
    const options: Intl.DateTimeFormatOptions = {
        ...baseOptions,
        ...(browserTimeZone ? { timeZone: browserTimeZone } : {}),
        ...(overrideOptions ?? {}),
    };
    return new Intl.DateTimeFormat(locale, options);
};

/**
 * 指定したプリセットで日付をフォーマットする
 * @param value Dateオブジェクトまたはparse可能な値
 * @param preset 使用するフォーマットプリセット
 * @param language 言語コード
 * @param overrideOptions オプションの上書き
 * @returns フォーマット済みの文字列
 */
export const formatDateByPreset = (
    value: Date | string | number,
    preset: DateFormatPreset,
    language?: string,
    overrideOptions?: Intl.DateTimeFormatOptions,
): string => {
    const formatter = createDateFormatter(language, preset, overrideOptions);
    const date = value instanceof Date ? value : new Date(value);
    return formatter.format(date);
};

/**
 * 外部で個別のIntl.DateTimeFormatOptionsを利用したい場合のヘルパー
 * @param value Dateオブジェクトまたはparse可能な値
 * @param language 言語コード
 * @param options Intlのオプション
 * @returns フォーマット済みの文字列
 */
export const formatDateWithOptions = (
    value: Date | string | number,
    language: string | undefined,
    options: Intl.DateTimeFormatOptions,
): string => {
    const preferredTimeZone = timeZoneProvider ? timeZoneProvider() : undefined;
    const browserTimeZone = preferredTimeZone ?? getBrowserTimeZone();
    const formatter = new Intl.DateTimeFormat(resolveDateLocale(language), {
        ...(browserTimeZone ? { timeZone: browserTimeZone } : {}),
        ...options,
    });
    const date = value instanceof Date ? value : new Date(value);
    return formatter.format(date);
};
